/**
 * History view (spec sections 29, 30, 54).
 *
 * One table for automated generations, manual Vids generations, imports
 * and episode renders, because they answer the same question: "what have
 * I already made, and how much did it cost?"
 */

import { h } from '../../utils/dom.js';
import { formatDateTime, formatDuration, relativeTime, formatUsd } from '../../utils/format.js';
import { summariseHistory } from '../../storage/history-store.js';
import { card, button, note, kv, segmented, sectionHeading } from '../components/common.js';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'manual-generation', label: 'Vids' },
  { id: 'automated-generation', label: 'Veo' },
  { id: 'import', label: 'Imports' },
  { id: 'episode-render', label: 'Episodes' },
];

const STATUS_CHIP = {
  COMPLETED: 'chip--ok',
  PROCESSING: 'chip--info',
  QUEUED: 'chip--info',
  FAILED: 'chip--danger',
  CANCELLED: 'chip--warn',
};

export function renderHistory(ctx) {
  const { store, actions } = ctx;
  const state = store.get();
  const root = h('div', { class: 'stack-lg' });
  const filter = state.historyFilter ?? 'all';
  const entries = state.history ?? [];

  const filtered = filter === 'all' ? entries : entries.filter((entry) => entry.kind === filter);
  const summary = summariseHistory(entries);

  root.append(
    sectionHeading('Generation history', `${summary.total} entries`, button('Clear history', {
      kind: 'danger',
      small: true,
      onClick: async () => {
        const ok = await actions.confirmSheet({
          title: 'Clear history?',
          message: 'This removes the local record of every generation and render. Stored clips are not deleted.',
          confirmLabel: 'Clear',
          kind: 'danger',
        });
        if (!ok) return;
        const { clearHistory } = await import('../../storage/history-store.js');
        await clearHistory();
        await actions.loadLibrary();
        actions.render();
      },
    })),
  );

  root.append(
    card([
      kv([
        ['Completed', String(summary.completed)],
        ['Failed', String(summary.failed)],
        ['Cancelled', String(summary.cancelled)],
        ['Estimated provider spend', summary.estimatedCostUsd ? formatUsd(summary.estimatedCostUsd) : '$0.000'],
      ]),
      note(
        'Cost figures are estimates from the published per-second rates at the time of the request, not invoices. Check your provider billing for the real number.',
        'info',
      ),
    ]),
  );

  root.append(
    segmented(FILTERS, filter, (id) => {
      store.set({ historyFilter: id });
      actions.render();
    }),
  );

  if (!filtered.length) {
    root.append(note('Nothing recorded yet. Generate a scene or render an episode and it will show up here.', 'info'));
    return root;
  }

  const list = h('div', { class: 'list' });
  for (const entry of filtered.slice(0, 120)) {
    list.append(
      h('div', { class: 'list__item' }, [
        h('div', { class: 'list__body' }, [
          h('div', { class: 'between' }, [
            h('div', { class: 'list__title' }, [entry.sceneName ?? entry.sceneId ?? entry.kind]),
            h('span', { class: `chip ${STATUS_CHIP[entry.status] ?? ''}` }, [entry.status]),
          ]),
          h('div', { class: 'tiny dim' }, [
            `${entry.kind} \u00b7 ${entry.providerId}${entry.model ? ` \u00b7 ${entry.model}` : ''}`,
          ]),
          h('div', { class: 'tiny dim' }, [
            `${formatDateTime(entry.createdAt)} (${relativeTime(entry.createdAt)})`,
            entry.durationSeconds ? ` \u00b7 ${formatDuration(entry.durationSeconds)}` : '',
            entry.estimatedCostUsd ? ` \u00b7 ${formatUsd(entry.estimatedCostUsd)}` : '',
          ]),
          entry.error ? note(entry.error, 'danger') : null,
          entry.notes ? h('div', { class: 'tiny dim break' }, [entry.notes]) : null,
          entry.prompt
            ? h('details', {}, [
                h('summary', { class: 'tiny', style: { cursor: 'pointer', color: 'var(--text-muted)' } }, ['Prompt']),
                h('pre', { class: 'prompt-box', style: { maxHeight: '180px', marginTop: '6px' } }, [entry.prompt]),
              ])
            : null,
        ]),
      ]),
    );
  }
  root.append(list);

  return root;
}
